'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Utensils, Bug, Leaf, ChevronRight, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { feedingService, harvestService, populationService } from '@/lib/services/farmService';

interface ActivityPreviewItem {
  id: string;
  type: 'feeding' | 'health' | 'harvest';
  icon: React.ElementType;
  color: string;
  user: string;
  bed: string;
  detail: string;
  time: string;
  timestamp: string;
}

const typeConfig = {
  feeding: { icon: Utensils, color: 'text-success bg-success-bg' },
  health: { icon: Bug, color: 'text-info bg-info-bg' },
  harvest: { icon: Leaf, color: 'text-primary bg-secondary' },
};

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

export default function RecentActivityPreview() {
  const [activities, setActivities] = useState<ActivityPreviewItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadActivities = useCallback(async () => {
    try {
      const [feedings, harvests, healthChecks] = await Promise.all([
        feedingService.getAll(),
        harvestService.getAll(),
        populationService.getAll(),
      ]);

      const items: ActivityPreviewItem[] = [];

      feedings.slice(0, 5).forEach((f) => {
        items.push({
          id: `feeding-${f.id}`,
          type: 'feeding',
          icon: Utensils,
          color: typeConfig.feeding.color,
          user: f.loggedBy ?? 'Unknown',
          bed: f.bedId,
          detail: `${f.feedType} ${f.feedQuantityKg} kg`,
          time: relativeTime(f.createdAt),
          timestamp: f.createdAt,
        });
      });

      harvests.slice(0, 5).forEach((h) => {
        items.push({
          id: `harvest-${h.id}`,
          type: 'harvest',
          icon: Leaf,
          color: typeConfig.harvest.color,
          user: h.loggedBy ?? 'Unknown',
          bed: h.bedId,
          detail: `Harvest ${h.wetWeightKg} kg — Grade ${h.qualityGrade}`,
          time: relativeTime(h.createdAt),
          timestamp: h.createdAt,
        });
      });

      healthChecks.slice(0, 5).forEach((p) => {
        const label = p.healthScore >= 8 ? 'excellent' : p.healthScore >= 6 ? 'good' : p.healthScore >= 4 ? 'fair' : 'flagged';
        items.push({
          id: `health-${p.id}`,
          type: 'health',
          icon: Bug,
          color: typeConfig.health.color,
          user: p.loggedBy ?? 'Unknown',
          bed: p.bedId,
          detail: `Health score ${p.healthScore} — ${label}`,
          time: relativeTime(p.createdAt),
          timestamp: p.createdAt,
        });
      });

      items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setActivities(items.slice(0, 5));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Real-time subscriptions
  useEffect(() => {
    const supabase = createClient();

    const feedingCh = supabase
      .channel('rt-activity-preview-feeding')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feeding_logs' }, loadActivities)
      .subscribe();

    const harvestCh = supabase
      .channel('rt-activity-preview-harvest')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'harvest_records' }, loadActivities)
      .subscribe();

    const healthCh = supabase
      .channel('rt-activity-preview-health')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'population_checks' }, loadActivities)
      .subscribe();

    return () => {
      supabase.removeChannel(feedingCh);
      supabase.removeChannel(harvestCh);
      supabase.removeChannel(healthCh);
    };
  }, [loadActivities]);

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-base font-semibold text-foreground">Recent Activity</h2>
        <Link href="/activity-feed" className="flex items-center gap-0.5 text-xs font-medium text-primary hover:underline">
          View all <ChevronRight size={13} />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-muted-foreground" />
        </div>
      ) : activities.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">No activity recorded yet</div>
      ) : (
        <div className="divide-y divide-border">
          {activities.map((a) => {
            const ItemIcon = a.icon;
            return (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${a.color}`}>
                  <ItemIcon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-foreground truncate">{a.user}</span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">{a.bed}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{a.detail}</p>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">{a.time}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}